import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Check, Loader2, Plus } from 'lucide-react'
import { loadPositionAssetListOptionChain } from '../../lib/positionAssetListStore.js'
import { openIbkrEphemeralQuoteBatch } from '../../lib/marketData/ibkrQuoteSubscriptionStore.js'
import { useIbkrPositionQuoteSnapshot } from '../../hooks/useIbkrQuoteSnapshot.js'
import { occIdentityKey } from '../../lib/occParser.js'
import { selectOptionQuoteWindow } from './optionChainWindow.js'

const QUOTE_WINDOW_SETTLE_MS = 2500
const QUOTE_ROW_RADIUS = 8
const SIDE_GRID = 'grid-cols-[1.75rem_minmax(5.5rem,1.05fr)_repeat(5,minmax(2.65rem,.68fr))]'

function expirationLabel(value) {
  if (!/^\d{8}$/.test(String(value || ''))) return value
  const date = new Date(`${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}T12:00:00Z`)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

function contractLabel(contract) {
  if (!contract) return '—'
  return `${contract.right === 'C' ? 'Call' : 'Put'} ${Number(contract.strike).toLocaleString()}`
}

function positiveQuoteValue(value) {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : null
}

function quotePrice(value) {
  const number = positiveQuoteValue(value)
  return number == null ? '—' : number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
}

function quoteCount(value) {
  if (value == null || value === '') return '—'
  const number = Number(value)
  if (!Number.isFinite(number) || number < 0) return '—'
  return new Intl.NumberFormat('en-US', { notation: number >= 1000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(number)
}

function quoteMark(quote) {
  const mark = positiveQuoteValue(quote?.mark)
  if (mark != null) return mark
  const bid = positiveQuoteValue(quote?.bid)
  const ask = positiveQuoteValue(quote?.ask)
  return bid != null && ask != null ? (bid + ask) / 2 : null
}

function quoteStateLabel(quote, settled) {
  if (!quote) return settled ? 'UNAVAILABLE' : 'WAITING'
  const freshness = String(quote.freshness || 'unknown').trim().toUpperCase()
  if (freshness === 'UNENTITLED') return 'UNENTITLED'
  const session = String(quote.session || '').trim().toLowerCase()
  const sessionLabel = session === 'regular' || session === 'rth'
    ? 'RTH'
    : session === 'extended' || session === 'eth'
      ? 'EXT'
      : session === 'closed'
        ? 'CLOSED'
        : session.toUpperCase() || 'SESSION?'
  return `${sessionLabel}·${freshness}`
}

export default function OptionChainPicker({
  underlying,
  watchlists = [],
  activeOptionIdentityKeys = new Set(),
  onBack,
  onOpenContract,
  onAddContract,
  onInspectWatchlists,
}) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null })
  const [expiry, setExpiry] = useState(null)
  const [qualificationCenter, setQualificationCenter] = useState(null)
  const [quotesByConid, setQuotesByConid] = useState({})
  const [quoteWindowSettled, setQuoteWindowSettled] = useState(false)
  const autoAdvanceExpiryRef = useRef(false)
  const atmRowRef = useRef(null)
  const chainScrollRef = useRef(null)
  const scrollFrameRef = useRef(null)
  const quoteTransitionRef = useRef(Promise.resolve())
  const [quoteCenterIndex, setQuoteCenterIndex] = useState(null)
  const { price: underlyingPrice } = useIbkrPositionQuoteSnapshot(underlying.symbol)

  useEffect(() => {
    setExpiry(null)
    setQualificationCenter(null)
    setQuoteCenterIndex(null)
    autoAdvanceExpiryRef.current = false
  }, [underlying.conid])

  useEffect(() => {
    setQuoteCenterIndex(null)
    setQuotesByConid({})
    setQuoteWindowSettled(false)
  }, [expiry])

  useEffect(() => {
    if (qualificationCenter == null && Number(underlyingPrice) > 0) {
      setQualificationCenter(Number(underlyingPrice))
    }
  }, [qualificationCenter, underlyingPrice])

  useEffect(() => {
    if (expiry && !(Number(qualificationCenter) > 0)) return undefined
    const controller = new AbortController()
    setState(current => ({ ...current, status: 'loading', error: null }))
    loadPositionAssetListOptionChain(underlying, expiry, {
      signal: controller.signal,
      center: qualificationCenter,
    })
      .then((data) => {
        if (controller.signal.aborted) return
        if (!expiry && data.expirations?.length) {
          autoAdvanceExpiryRef.current = true
          setExpiry(data.expirations[0])
          setState({ status: 'loading', data, error: null })
          return
        }
        if (expiry && autoAdvanceExpiryRef.current && !data.rows?.length) {
          const currentIndex = data.expirations?.indexOf(expiry) ?? -1
          const nextExpiry = currentIndex >= 0 ? data.expirations?.[currentIndex + 1] : null
          if (nextExpiry) {
            setExpiry(nextExpiry)
            setState({ status: 'loading', data, error: null })
            return
          }
        }
        autoAdvanceExpiryRef.current = false
        setState({ status: 'ready', data, error: null })
      })
      .catch((error) => {
        if (!controller.signal.aborted) setState({ status: 'error', data: null, error })
      })
    return () => controller.abort()
  }, [expiry, qualificationCenter, underlying])

  const expirations = state.data?.expirations || []
  const rows = useMemo(() => state.data?.rows || [], [state.data?.rows])
  const atmStrike = useMemo(() => {
    if (!(Number(underlyingPrice) > 0) || rows.length === 0) return null
    return rows.reduce((nearest, row) => (
      Math.abs(Number(row.strike) - Number(underlyingPrice))
        < Math.abs(Number(nearest.strike) - Number(underlyingPrice))
        ? row
        : nearest
    )).strike
  }, [rows, underlyingPrice])
  const atmIndex = useMemo(() => rows.findIndex(row => Number(row.strike) === Number(atmStrike)), [atmStrike, rows])
  const effectiveQuoteCenterIndex = quoteCenterIndex == null ? (atmIndex >= 0 ? atmIndex : 0) : quoteCenterIndex
  const quotedRows = useMemo(() => {
    return selectOptionQuoteWindow(rows, effectiveQuoteCenterIndex, QUOTE_ROW_RADIUS)
  }, [effectiveQuoteCenterIndex, rows])

  useEffect(() => {
    if (state.status !== 'ready' || quotedRows.length === 0) return undefined
    setQuoteWindowSettled(false)
    const contracts = quotedRows.flatMap(row => [row.call, row.put]).filter(Boolean)
    let disposed = false
    let resource = null
    const started = quoteTransitionRef.current.catch(() => {}).then(async () => {
      if (disposed) return
      resource = await openIbkrEphemeralQuoteBatch({
        contracts,
        onQuote: quote => {
          if (disposed) return
          const conid = Number(quote?.contract?.conid)
          if (!Number.isInteger(conid) || conid <= 0) return
          setQuotesByConid(current => ({ ...current, [conid]: quote }))
        },
      })
      if (disposed) await resource.release()
    }).catch(() => {
      if (!disposed) setQuoteWindowSettled(true)
    })
    quoteTransitionRef.current = started
    const settleTimer = setTimeout(() => setQuoteWindowSettled(true), QUOTE_WINDOW_SETTLE_MS)
    return () => {
      disposed = true
      clearTimeout(settleTimer)
      quoteTransitionRef.current = started.catch(() => {}).then(() => resource?.release())
    }
  }, [expiry, quotedRows, state.status])

  useEffect(() => {
    if (state.status !== 'ready' || atmStrike == null) return
    atmRowRef.current?.scrollIntoView?.({ block: 'center' })
  }, [atmStrike, expiry, state.status])

  useEffect(() => () => {
    if (scrollFrameRef.current != null) window.cancelAnimationFrame(scrollFrameRef.current)
  }, [])

  const handleChainScroll = () => {
    if (scrollFrameRef.current != null) return
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null
      const viewport = chainScrollRef.current
      if (!viewport) return
      const viewportRect = viewport.getBoundingClientRect()
      const center = viewportRect.top + (viewportRect.height / 2)
      const rowElements = [...viewport.querySelectorAll('[data-option-chain-row-index]')]
      const nearest = rowElements.reduce((best, element) => {
        const rect = element.getBoundingClientRect()
        const distance = Math.abs((rect.top + (rect.height / 2)) - center)
        return distance < best.distance
          ? { index: Number(element.dataset.optionChainRowIndex), distance }
          : best
      }, { index: effectiveQuoteCenterIndex, distance: Number.POSITIVE_INFINITY })
      setQuoteCenterIndex(current => current === nearest.index ? current : nearest.index)
    })
  }

  return (
    <div data-testid="asset-list-option-chain" className="flex h-full min-h-0 flex-col">
      <div data-option-chain-drag-handle className="flex cursor-move select-none items-center gap-3 border-b border-border px-3 py-2">
        <button type="button" aria-label="Back to symbol results" onClick={onBack} className="rounded p-1.5 text-inkMuted hover:bg-canvas2 hover:text-ink"><ArrowLeft size={18} /></button>
        <div>
          <div className="font-sans text-base font-semibold">{underlying.symbol} Options</div>
          <div className="font-mono text-[9px] text-inkMuted">Exact listed contracts{underlyingPrice != null ? ` · ${underlying.symbol} ${Number(underlyingPrice).toLocaleString(undefined, { maximumFractionDigits: 4 })}` : ''}</div>
        </div>
      </div>
      {expirations.length > 0 && (
        <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-border px-3 py-2" aria-label="Option expirations">
          {expirations.map(value => (
            <button key={value} type="button" aria-label={`Expiration ${value}`} onClick={() => { autoAdvanceExpiryRef.current = false; setExpiry(value) }} className={`shrink-0 rounded px-3 py-1.5 font-mono text-xs ${expiry === value ? 'bg-ink text-canvas' : 'bg-canvas2 text-inkMuted hover:text-ink'}`}>
              {expirationLabel(value)}
            </button>
          ))}
        </div>
      )}
      <div className="grid shrink-0 grid-cols-[1fr_5.5rem_1fr] border-b border-border bg-canvas2 font-mono text-[10px] font-semibold uppercase tracking-wide text-inkMuted">
        <div className="px-3 py-1.5 text-center">Calls</div>
        <div className="border-x border-border px-3 py-1.5 text-center">Strike</div>
        <div className="px-3 py-1.5 text-center">Puts</div>
      </div>
      <div className="grid shrink-0 grid-cols-[1fr_5.5rem_1fr] border-b border-border bg-canvas font-mono text-[10px] uppercase tracking-wide text-inkMuted">
        <QuoteSideHeader />
        <div className="border-x border-border" />
        <QuoteSideHeader />
      </div>
      {state.status === 'loading' && <div className="flex items-center justify-center gap-2 py-20 font-mono text-xs text-inkMuted"><Loader2 size={14} className="animate-spin" />Loading exact IBKR contracts…</div>}
      {state.status === 'error' && <div role="alert" className="py-20 text-center font-mono text-xs text-ember">{state.error?.message || 'Option chain unavailable'}</div>}
      {state.status === 'ready' && rows.length === 0 && <div className="py-20 text-center font-mono text-xs text-inkMuted">No contracts returned for this expiration.</div>}
      {state.status === 'ready' && rows.length > 0 && (
        <div ref={chainScrollRef} onScroll={handleChainScroll} className="min-h-0 flex-1 overflow-y-auto">
          {rows.map((row, index) => {
            const isAtm = Number(row.strike) === Number(atmStrike)
            const showPriceLine = underlyingPrice != null
              && Number(row.strike) > Number(underlyingPrice)
              && (index === 0 || Number(rows[index - 1].strike) <= Number(underlyingPrice))
            return (
              <div key={row.strike} ref={isAtm ? atmRowRef : null} data-option-chain-row-index={index}>
                {showPriceLine && <div className="relative h-px bg-ink/60" aria-label={`${underlying.symbol} current price ${underlyingPrice}`}><span className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 rounded bg-ink px-2 py-0.5 font-mono text-[9px] text-canvas">{underlying.symbol} {Number(underlyingPrice).toLocaleString(undefined, { maximumFractionDigits: 4 })}</span></div>}
                <div className="grid grid-cols-[1fr_5.5rem_1fr] border-b border-border/70">
                  <ContractCell contract={row.call} quote={quotesByConid[row.call?.conid]} quoteWindowSettled={quoteWindowSettled} active={activeOptionIdentityKeys.has(occIdentityKey(row.call?.localSymbol))} inTheMoney={underlyingPrice != null && Number(row.strike) < Number(underlyingPrice)} watchlists={watchlists} onOpenContract={onOpenContract} onAddContract={onAddContract} onInspectWatchlists={onInspectWatchlists} />
                  <div aria-label={isAtm ? `ATM strike ${Number(row.strike).toLocaleString()}` : undefined} className="flex items-center justify-center border-x border-border bg-canvas2 px-2 py-1 font-mono text-xs text-ink">{Number(row.strike).toLocaleString()}</div>
                  <ContractCell contract={row.put} quote={quotesByConid[row.put?.conid]} quoteWindowSettled={quoteWindowSettled} active={activeOptionIdentityKeys.has(occIdentityKey(row.put?.localSymbol))} inTheMoney={underlyingPrice != null && Number(row.strike) > Number(underlyingPrice)} watchlists={watchlists} onOpenContract={onOpenContract} onAddContract={onAddContract} onInspectWatchlists={onInspectWatchlists} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function QuoteSideHeader() {
  return (
    <div className={`grid ${SIDE_GRID} items-center px-0.5 py-1 text-center`}>
      <span /><span>Contract</span><span>Bid</span><span>Ask</span><span>Mark</span><span>Vol</span><span>OI</span>
    </div>
  )
}

function ContractCell({ contract, quote = null, quoteWindowSettled = false, active = false, inTheMoney = false, watchlists, onOpenContract, onAddContract, onInspectWatchlists }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [membershipListIds, setMembershipListIds] = useState(new Set())
  const [membershipStatus, setMembershipStatus] = useState('idle')
  const [addingListId, setAddingListId] = useState(null)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!menuOpen) return undefined
    const close = event => {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [menuOpen])

  if (!contract) return <div className="px-3 py-2 text-center text-inkMuted">—</div>
  const stateLabel = quoteStateLabel(quote, quoteWindowSettled)

  const toggleWatchlists = async () => {
    const nextOpen = !menuOpen
    setMenuOpen(nextOpen)
    if (!nextOpen || membershipStatus === 'loading') return
    setMembershipStatus('loading')
    try {
      const listIds = await onInspectWatchlists?.(contract)
      setMembershipListIds(new Set(listIds || []))
      setMembershipStatus('ready')
    } catch {
      setMembershipStatus('error')
    }
  }

  const addToWatchlist = async (listId) => {
    if (membershipListIds.has(listId) || addingListId) return
    setAddingListId(listId)
    try {
      await onAddContract?.(contract, listId)
      setMembershipListIds(current => new Set([...current, listId]))
      setMenuOpen(false)
    } catch {
      // PositionSwitcher owns the visible error message.
    } finally {
      setAddingListId(null)
    }
  }

  return (
    <div className={`grid min-w-0 ${SIDE_GRID} items-center px-0.5 text-center font-mono ${inTheMoney ? 'bg-ember/10' : ''}`}>
      <div ref={menuRef} className="relative flex items-center justify-center">
        <button type="button" aria-label={`Add ${contract.localSymbol} to watchlist`} title="Add to watchlist" onClick={() => void toggleWatchlists()} className="rounded p-1 text-inkMuted hover:bg-canvas2 hover:text-ink">
          <Plus size={13} />
        </button>
        {menuOpen && (
          <div role="menu" aria-label={`Watchlists for ${contract.localSymbol}`} className="absolute left-0 top-full z-50 mt-1 min-w-40 rounded border border-border bg-canvas p-1 text-left shadow-2xl">
            {membershipStatus === 'loading' && <div className="flex items-center gap-2 px-2 py-1.5 text-[10px] text-inkMuted"><Loader2 size={12} className="animate-spin" />Loading watchlists…</div>}
            {membershipStatus !== 'loading' && watchlists.length === 0 && <div className="px-2 py-1.5 text-[10px] text-inkMuted">No watchlists</div>}
            {membershipStatus !== 'loading' && watchlists.map(list => {
              const alreadyAdded = membershipListIds.has(list.listId)
              const adding = addingListId === list.listId
              return (
                <button key={list.listId} type="button" role="menuitem" aria-label={alreadyAdded ? `${contract.localSymbol} is already in ${list.name}` : `Add ${contract.localSymbol} to ${list.name}`} disabled={alreadyAdded || adding} onClick={() => void addToWatchlist(list.listId)} className="flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-[11px] text-ink hover:bg-canvas2 disabled:text-inkMuted">
                  <span className="truncate">{list.name}</span>
                  {adding ? <Loader2 size={12} className="animate-spin" /> : alreadyAdded ? <Check size={12} /> : null}
                </button>
              )
            })}
            {membershipStatus === 'error' && <div className="px-2 py-1.5 text-[10px] text-ember">Could not check memberships</div>}
          </div>
        )}
      </div>
      <button type="button" aria-label={`Open ${contract.localSymbol} chart`} title={`${stateLabel} · multiplier ${contract.multiplier || 'unavailable'}`} onClick={() => onOpenContract(contract)} className="relative flex min-w-0 items-center justify-center rounded px-0.5 py-1 text-center text-[11px] text-ink hover:bg-canvas2">
        {active && <span aria-label="Active position" title="Active position" className="absolute left-0 top-1/2 h-4 w-2.5 -translate-y-1/2 bg-forest" style={{ clipPath: 'polygon(0 0, 100% 0, 64% 50%, 100% 100%, 0 100%)' }} />}
        <span>{contractLabel(contract)}</span>
      </button>
      <span className="text-[10px] tabular-nums text-ink">{quotePrice(quote?.bid)}</span>
      <span className="text-[10px] tabular-nums text-ink">{quotePrice(quote?.ask)}</span>
      <span className="text-[10px] tabular-nums text-ink">{quotePrice(quoteMark(quote))}</span>
      <span className="text-[10px] tabular-nums text-inkMuted">{quoteCount(quote?.volume)}</span>
      <span className="text-[10px] tabular-nums text-inkMuted">{quoteCount(quote?.openInterest)}</span>
    </div>
  )
}
